import { useMemo, useState } from 'react';

import { Page } from '@occc-app/design-system';
import { DateRangeSelector } from '@occc/ui-design-system';
import { useFooterInfoBanner } from '@occc-app/design-system/layout/AppLayout/footer-info-banner/footer-info-banner.context';
import { hasAnyNonZeroProps } from '@occc/utils';

import RetailerNetworkTobaccoPerformanceDetailsPage from './retailer-network-tobacco-performance-details-page.component';
import { usePerformanceDetails } from './hooks/use-performance-details';

import { routeCreator } from '../retailer-connect.utils';
import { withRetailerNetwork, WithRetailerNetworkProps } from '../hocs/with-retailer-network.hoc';
import { useBreadcrumbLinks } from '../hooks/use-breadcrumb-links.hook';
import { RetailerNetworkDataLoading } from '../components/retailer-network-data-loading.component';
import { RetailerNetworkDataPlaceholder } from '../components/retailer-network-data-placeholder.component';
import { tobaccoEmptyDataDescription, tobaccoEmptyDataTitle } from '../retailer-connect.copies';

const PAGE_TITLE = 'Program Performance Details';
const FOOTER_INFO_BANNER_MESSAGE =
  'Estimates based on submitted data, not financial statements or the actual reimbursed value';

const RetailerNetworkTobaccoPerformanceDetailsPageContainer = ({
  retailerNetwork,
}: WithRetailerNetworkProps) => {
  const breadcrumbLinks = useMemo(
    () => [
      {
        label: 'Program Performance',
        to: `${routeCreator.performance(retailerNetwork.uuid)}/tobacco-program`,
      },
      { label: 'Details' },
    ],
    [retailerNetwork.uuid],
  );

  useFooterInfoBanner(FOOTER_INFO_BANNER_MESSAGE);
  useBreadcrumbLinks(breadcrumbLinks);

  const {
    activeMetricType,
    setActiveMetricType,
    metricDateRanges,
    metricsOverview,
    startDate,
    endDate,
    onDateChange,
  } = usePerformanceDetails(retailerNetwork.uuid);

  const [isDateRangeDisabled, setIsDateRangeDisabled] = useState(false);

  const isZeroData = useMemo(() => {
    const is = metricsOverview?.data && !hasAnyNonZeroProps(metricsOverview.data);
    setIsDateRangeDisabled(true);
    return is;
  }, [metricsOverview.data]);

  return (
    <Page
      browserTabTitle={PAGE_TITLE}
      pageHeaderProps={{
        title: PAGE_TITLE,
        titleTestId: 'rc-tobacco-performance-details-title',
      }}
      containerProps={{ variant: 'md' }}
      actions={
        <DateRangeSelector
          value={{ startDate, endDate }}
          onChange={(value) => {
            onDateChange(value.startDate, value.endDate);
          }}
          disabled={isDateRangeDisabled}
          dateRangePresets={metricDateRanges}
        />
      }
    >
      <RetailerNetworkDataLoading
        isLoading={metricsOverview.loading}
        hasData={!!metricsOverview.data}
        hasError={!!metricsOverview.error}
      >
        {isZeroData ? (
          <RetailerNetworkDataPlaceholder
            title={tobaccoEmptyDataTitle}
            description={tobaccoEmptyDataDescription}
          />
        ) : (
          !!metricsOverview.data && (
            <RetailerNetworkTobaccoPerformanceDetailsPage
              metricItems={metricsOverview.data}
              metricType={activeMetricType}
              onMetricTypeChange={setActiveMetricType}
              startDate={startDate}
              endDate={endDate}
              retailerNetworkUuid={retailerNetwork.uuid}
            />
          )
        )}
      </RetailerNetworkDataLoading>
    </Page>
  );
};

export default withRetailerNetwork(RetailerNetworkTobaccoPerformanceDetailsPageContainer);
